export type StaffListItem = {
  legacy_staff_id: number;
  legacy_name: string | null;
  legacy_email: string | null;
  live_staff_id: number | null;
  live_name: string | null;
  live_active: boolean;
  has_future: boolean;
  future_sessions?: number;
  future_slots?: number;
  // Present when the list was fetched with class_roster != "all".
  active_student_count?: number;
  imported_classes: number;
  status:
    | "not_imported"
    | "unmapped"
    | "no_future_classes"
    | "imported"
    | "partial";
  eligible: boolean;
};

export type StaffListResponse = {
  items: StaffListItem[];
  page: number;
  per_page: number;
  total: number;
  cutoff: string;
};

export type SummaryResponse = {
  cutoff: string;
  total_active: number;
  total_eligible: number;
  imported: number;
  partial: number;
  not_imported: number;
  unmapped: number;
  errors: number;
  // §8 — count of eligible staff with ≥1 slot that has ≥1 distinct valid
  // active student in future/current day_details.
  non_empty_class_staff?: number;
};

export type PreviewClass = {
  legacy_slot: string;
  existing_class_id: number | null;
  from_time: string;
  to_time: string;
  days: number[];
  subject_tokens: string[];
  subject_pivot_ids: number[];
  missing_subjects: { token: string; label: string }[];
  // §3 capacity fields — capacity == proposed_capacity == max(legacy, active).
  capacity: number;
  proposed_capacity?: number;
  legacy_capacity?: number;
  ambiguous_match?: boolean;
  ambiguous_match_ids?: number[];
  active_roster_count: number;
  location_id: number | null;
  location_name: string | null;
  location_source: "live_student_location" | "legacy_study_mode" | null;
  location_conflicts: Array<{
    source: "per_student" | "campus_split";
    live_location?: string;
    legacy_location?: string;
    legacy_study_mode?: number | null;
    location_id?: number;
    location_name?: string | null;
    student_count?: number;
    student_ids?: number[];
  }>;
  class_mode: string;
  class_type: string;
  students: {
    legacy_student_id: number;
    live_student_id: number;
    name: string | null;
    location_id: number | null;
    location_source: string | null;
    live_location_id: number | null;
    legacy_study_mode: number | null;
    legacy_study_mode_location_id: number | null;
    location_conflict: null | {
      live_location_id: number;
      legacy_location_id: number;
      legacy_study_mode: number | null;
    };
    study_mode: string | null;
    is_dropped: boolean;
    subject_ids: string;
    subject_pivot_ids: number[];
    missing_subjects: string[];
    active_dates_count: number;
    fees: number;
  }[];
  unmapped_students: { legacy_student_id: number; name?: string | null }[];
  dropped_students: {
    legacy_student_id: number;
    live_student_id: number | null;
    name: string | null;
    legacy_status: string | null;
    reason: string;
  }[];
  excluded_counts: {
    released: number;
    cancelled: number;
    makeup: number;
    dropped: number;
    unmapped: number;
  };
  session_count_source: number;
  diff: { to_add: number[]; to_soft_remove: number[]; preserved: number[] };
  warnings: string[];
  blocking_reasons: string[];
  action: "create" | "update" | "skip" | "blocked";
};

export type PreviewResponse = {
  legacy_staff_id: number;
  live_staff_id: number | null;
  staff_name: string | null;
  live_staff_name: string | null;
  cutoff_date: string | null;
  groups_count: number;
  active_students: number;
  future_sessions: number;
  blocked_classes: number;
  status: "ok" | "partial" | "blocked";
  classes: PreviewClass[];
  blocking_reasons?: string[];
};

export type ImportResult = {
  staff_log_id: number;
  legacy_staff_id: number;
  live_staff_id: number | null;
  status: string;
  counts?: {
    classes_created: number;
    classes_updated: number;
    classes_skipped: number;
    classes_blocked: number;
    schedules_created: number;
    enrolments_created: number;
    enrolments_soft_removed: number;
    occurrences_created: number;
  };
  preview: PreviewResponse;
};

export const WEEKDAY_LABELS: Record<number, string> = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
};
